import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import NegotiationsPanel from './negotiations/NegotiationsPanel';
import DocumentsPanel from './documents/DocumentsPanel';

const SEGMENTS = ['Tecnologia','Saúde','Educação','Varejo','Indústria','Financeiro','Agronegócio','Construção','Logística','Serviços','Outros'];
const SIZES = [
  { value: 'micro', label: 'Micro (até 9 func.)' },
  { value: 'pequena', label: 'Pequena (10-49)' },
  { value: 'media', label: 'Média (50-249)' },
  { value: 'grande', label: 'Grande (250+)' },
];

const EMPTY_FORM = {
  name: '', cnpj: '', fantasy_name: '', segment: '', size: '',
  website: '', phone: '', email: '', address: '', city: '', state: '', notes: '',
  responsible_id: '', managing_company_id: ''
};

export default function Companies({ api }) {
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [managingCompanies, setManagingCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyLeads, setCompanyLeads] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [detailTab, setDetailTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [showNewManagingCompany, setShowNewManagingCompany] = useState(false);
  const [newManagingCompanyName, setNewManagingCompanyName] = useState('');
  const [savingManagingCompany, setSavingManagingCompany] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim().length >= 2) params.search = search.trim();
      if (segmentFilter) params.segment = segmentFilter;
      const res = await api.get('/companies', { params });
      setCompanies(res.data || []);
    } catch {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, [search, segmentFilter, api]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => {});
  }, [api]);

  const fetchManagingCompanies = useCallback(async () => {
    try {
      const res = await api.get('/managing-companies');
      setManagingCompanies(res.data || []);
    } catch {
      // não-crítico para a tela carregar
    }
  }, [api]);

  useEffect(() => { fetchManagingCompanies(); }, [fetchManagingCompanies]);

  const handleCreateManagingCompany = async () => {
    const name = newManagingCompanyName.trim();
    if (!name) return;
    setSavingManagingCompany(true);
    try {
      const res = await api.post('/managing-companies', { name });
      setManagingCompanies(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setForm(prev => ({ ...prev, managing_company_id: res.data.id }));
      setNewManagingCompanyName('');
      setShowNewManagingCompany(false);
      toast.success('Empresa responsável criada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar empresa responsável');
    } finally {
      setSavingManagingCompany(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowNewManagingCompany(false);
    setNewManagingCompanyName('');
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '', cnpj: c.cnpj || '', fantasy_name: c.fantasy_name || '',
      segment: c.segment || '', size: c.size || '', website: c.website || '',
      phone: c.phone || '', email: c.email || '', address: c.address || '',
      city: c.city || '', state: c.state || '', notes: c.notes || '',
      responsible_id: c.responsible_id || '', managing_company_id: c.managing_company_id || ''
    });
    setShowNewManagingCompany(false);
    setNewManagingCompanyName('');
    setShowModal(true);
  };

  const handleSave = async () => {
    const errors = [
      !form.name.trim()          && 'Razão Social é obrigatória',
      form.cnpj.length !== 18    && 'CNPJ inválido',
      form.phone.length < 14     && 'Telefone inválido',
      !form.email.trim()         && 'E-mail é obrigatório',
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && 'E-mail inválido',
      !form.address.trim()       && 'Endereço é obrigatório',
      !form.city.trim()          && 'Cidade é obrigatória',
      form.state.trim().length < 2 && 'Estado é obrigatório',
    ].filter(Boolean);

    if (errors.length > 0) {
      errors.forEach(msg => toast.error(msg));
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.responsible_id) delete payload.responsible_id;
      if (!payload.segment) delete payload.segment;
      if (!payload.size) delete payload.size;
      if (!payload.managing_company_id) delete payload.managing_company_id;

      if (editing) {
        const managingCompanyChanged = 'managing_company_id' in payload
          && payload.managing_company_id !== (editing.managing_company_id || '');
        await api.put(`/companies/${editing.id}`, payload);
        toast.success(
          managingCompanyChanged
            ? 'Empresa atualizada! A troca de empresa responsável ficou registrada no histórico.'
            : 'Empresa atualizada!'
        );
        if (showDetail && selectedCompany?.id === editing.id) openDetail({ id: editing.id });
      } else {
        await api.post('/companies', payload);
        toast.success('Empresa criada!');
      }
      setShowModal(false);
      fetchCompanies();
    } catch {
      toast.error('Erro ao salvar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Excluir "${c.name}"? Os leads vinculados serão desvinculados.`)) return;
    try {
      await api.delete(`/companies/${c.id}`);
      toast.success('Empresa excluída');
      fetchCompanies();
      if (showDetail && selectedCompany?.id === c.id) setShowDetail(false);
    } catch {
      toast.error('Erro ao excluir empresa');
    }
  };

  const openDetail = async (c) => {
    setSelectedCompany(c);
    setDetailTab('overview');
    setShowDetail(true);
    try {
      const res = await api.get(`/companies/${c.id}`);
      setCompanyLeads(res.data.leads || []);
      setSelectedCompany(res.data);
    } catch {
      setCompanyLeads([]);
    }
  };

  const closeDetail = () => {
    setShowDetail(false);
    fetchCompanies();
  };

  const f = (val, key) => setForm(prev => ({ ...prev, [key]: val }));

  const isFormValid = () => (
    form.name.trim() !== '' &&
    form.cnpj.length === 18 &&
    form.phone.length >= 14 &&
   /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.address.trim() !== '' &&
    form.city.trim() !== '' &&
    form.state.trim().length === 2
  );

  const sizeLabel = (v) => SIZES.find(s => s.value === v)?.label || v || '—';
  const userLabel = (id) => users.find(u => u.id === id)?.name || '—';
  const managingCompanyLabel = (id) => managingCompanies.find(m => m.id === id)?.name || '—';

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-6">
      {showDetail && selectedCompany ? (
        <div className="space-y-6">
          {/* Detail top bar */}
          <div className="flex items-center justify-between">
            <button
              onClick={closeDetail}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-blue-600 transition-colors"
            >
              ← Empresas
            </button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => openEdit(selectedCompany)}>✏️ Editar</Button>
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(selectedCompany)}>🗑️ Excluir</Button>
            </div>
          </div>

          {/* Detail title */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 flex-wrap">
              🏢 {selectedCompany.name}
              {selectedCompany.segment && <Badge variant="outline">{selectedCompany.segment}</Badge>}
              {selectedCompany.size && <Badge variant="outline" className="bg-gray-50">{sizeLabel(selectedCompany.size)}</Badge>}
              {selectedCompany.managing_company_name && (
                <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">🤝 {selectedCompany.managing_company_name}</Badge>
              )}
            </h1>
          </div>

          {/* Detail tabs */}
          <div className="border-b border-gray-200">
            <div className="flex space-x-6">
              {[
                { id: 'overview', label: '👁️ Visão Geral' },
                { id: 'negotiations', label: '🤝 Negociações' },
                { id: 'documents', label: '📁 Documentos' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setDetailTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    detailTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Detail tab content */}
          {detailTab === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {selectedCompany.fantasy_name && <div><span className="text-gray-500">Nome fantasia:</span> <span className="font-medium">{selectedCompany.fantasy_name}</span></div>}
                {selectedCompany.cnpj && <div><span className="text-gray-500">CNPJ:</span> <span className="font-medium">{selectedCompany.cnpj}</span></div>}
                {selectedCompany.phone && <div><span className="text-gray-500">Telefone:</span> <span className="font-medium">{selectedCompany.phone}</span></div>}
                {selectedCompany.email && <div><span className="text-gray-500">E-mail:</span> <span className="font-medium">{selectedCompany.email}</span></div>}
                {selectedCompany.website && <div><span className="text-gray-500">Website:</span> <a href={selectedCompany.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{selectedCompany.website}</a></div>}
                {(selectedCompany.city || selectedCompany.state) && <div><span className="text-gray-500">Local:</span> <span className="font-medium">{[selectedCompany.address, selectedCompany.city, selectedCompany.state].filter(Boolean).join(', ')}</span></div>}
                {selectedCompany.responsible_id && <div><span className="text-gray-500">Responsável:</span> <span className="font-medium">{userLabel(selectedCompany.responsible_id)}</span></div>}
                {selectedCompany.managing_company_name && (
                  <div><span className="text-gray-500">Atendido por:</span> <span className="font-medium">🤝 {selectedCompany.managing_company_name}</span></div>
                )}
              </div>

              {selectedCompany.notes && (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                  <span className="font-medium text-gray-500 block mb-1">Observações:</span>
                  {selectedCompany.notes}
                </div>
              )}

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">👥 Leads vinculados ({companyLeads.length})</h4>
                {companyLeads.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Nenhum lead vinculado ainda</p>
                ) : (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {companyLeads.map(l => (
                      <div key={l.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                        <div>
                          <span className="font-medium">{l.name}</span>
                          {l.email && <span className="text-gray-400 ml-2">{l.email}</span>}
                        </div>
                        <Badge variant="outline" className="text-xs">{l.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedCompany.management_history && selectedCompany.management_history.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">🤝 Histórico de empresa responsável</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedCompany.management_history.map(h => (
                      <div key={h.id} className="flex items-start justify-between px-3 py-2 bg-gray-50 rounded-lg text-xs">
                        <div>
                          <span className="text-gray-500">{h.from_managing_company_name || '— (sem empresa)'}</span>
                          <span className="mx-1.5">→</span>
                          <span className="font-medium">{h.to_managing_company_name || '— (sem empresa)'}</span>
                          <div className="text-gray-400 mt-0.5">por {h.changed_by_name || 'sistema'}</div>
                        </div>
                        <span className="text-gray-400 shrink-0 ml-2">{formatDate(h.changed_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {detailTab === 'negotiations' && (
            <NegotiationsPanel entityType="company" entityId={selectedCompany.id} api={api} users={users} />
          )}

          {detailTab === 'documents' && (
            <DocumentsPanel entityType="company" entityId={selectedCompany.id} api={api} />
          )}
        </div>
      ) : (
        <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🏢 Empresas</h1>
          <p className="text-sm text-gray-500 mt-1">{companies.length} empresa{companies.length !== 1 ? 's' : ''} cadastrada{companies.length !== 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
          + Nova Empresa
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          className="w-64"
          placeholder="Buscar por nome, fantasia, CNPJ..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Select value={segmentFilter || '__all__'} onValueChange={v => setSegmentFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Todos os segmentos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os segmentos</SelectItem>
            {SEGMENTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {(search || segmentFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setSegmentFilter(''); }}>
            Limpar
          </Button>
        )}
      </div>

      {/* Company Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="h-40 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : companies.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🏢</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nenhuma empresa encontrada</h3>
          <p className="text-gray-500 text-sm mb-6">Crie o cadastro de empresas para vincular seus leads e analisar desempenho por conta.</p>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">+ Criar primeira empresa</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map(c => (
            <Card key={c.id} className="hover:shadow-md transition-shadow cursor-pointer border border-gray-200">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{c.name}</CardTitle>
                    {c.fantasy_name && c.fantasy_name !== c.name && (
                      <p className="text-xs text-gray-400 truncate">{c.fantasy_name}</p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={e => { e.stopPropagation(); openEdit(c); }} className="text-gray-400 hover:text-blue-600 text-xs p-1 rounded hover:bg-blue-50" title="Editar">✏️</button>
                    <button onClick={e => { e.stopPropagation(); handleDelete(c); }} className="text-gray-400 hover:text-red-600 text-xs p-1 rounded hover:bg-red-50" title="Excluir">🗑️</button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0" onClick={() => openDetail(c)}>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {c.segment && <Badge variant="outline" className="text-xs">{c.segment}</Badge>}
                    {c.size && <Badge variant="outline" className="text-xs bg-gray-50">{sizeLabel(c.size)}</Badge>}
                    {c.managing_company_name && (
                      <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                        🤝 {c.managing_company_name}
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 text-xs text-gray-500">
                    {c.city && <span>📍 {c.city}{c.state ? `, ${c.state}` : ''}</span>}
                    {c.phone && <span>📞 {c.phone}</span>}
                    {c.website && <span className="truncate">🌐 {c.website.replace(/^https?:\/\//, '')}</span>}
                    {c.cnpj && <span>🆔 {c.cnpj}</span>}
                  </div>
                  <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                    <span className="text-xs text-gray-500">
                      👥 <strong>{c.lead_count || 0}</strong> lead{c.lead_count !== 1 ? 's' : ''}
                    </span>
                    {c.responsible_id && (
                      <span className="text-xs text-gray-400">resp: {userLabel(c.responsible_id)}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Empresa' : 'Nova Empresa'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1">
              <Label>Razão Social <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={e => f(e.target.value, 'name')} placeholder="Razão Social" />
            </div>
            <div className="space-y-1">
              <Label>Nome Fantasia</Label>
              <Input value={form.fantasy_name} onChange={e => f(e.target.value, 'fantasy_name')} placeholder="Nome fantasia" />
            </div>
            <div className="space-y-1">
              <Label>CNPJ <span className="text-red-500">*</span></Label>
              <Input value={form.cnpj} onChange={e => f(e.target.value.replace(/\D/g, '').slice(0, 14).replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2'), 'cnpj')} placeholder="00.000.000/0000-00" />
            </div>
            <div className="space-y-1">
              <Label>Segmento</Label>
              <Select value={form.segment || '__none__'} onValueChange={v => f(v === '__none__' ? '' : v, 'segment')}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhum —</SelectItem>
                  {SEGMENTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Porte</Label>
              <Select value={form.size || '__none__'} onValueChange={v => f(v === '__none__' ? '' : v, 'size')}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhum —</SelectItem>
                  {SIZES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Telefone <span className="text-red-500">*</span></Label>
              <Input value={form.phone} onChange={e => f(e.target.value.replace(/\D/g, '').slice(0, 11).replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2'), 'phone')} placeholder="(11) 99999-9999" />
            </div>
            <div className="space-y-1">
              <Label>E-mail <span className="text-red-500">*</span></Label>
              <Input type="email" value={form.email} onChange={e => f(e.target.value, 'email')} placeholder="contato@empresa.com" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Website</Label>
              <Input value={form.website} onChange={e => f(e.target.value, 'website')} placeholder="https://empresa.com.br" />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Endereço <span className="text-red-500">*</span></Label>
              <Input value={form.address} onChange={e => f(e.target.value, 'address')} placeholder="Rua, número, bairro" />
            </div>
            <div className="space-y-1">
              <Label>Cidade <span className="text-red-500">*</span></Label>
              <Input value={form.city} onChange={e => f(e.target.value, 'city')} placeholder="São Paulo" />
            </div>
            <div className="space-y-1">
              <Label>Estado <span className="text-red-500">*</span></Label>
              <Input value={form.state} onChange={e => f(e.target.value, 'state')} placeholder="SP" maxLength={2} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Responsável Interno</Label>
              <Select value={form.responsible_id || '__none__'} onValueChange={v => f(v === '__none__' ? '' : v, 'responsible_id')}>
                <SelectTrigger><SelectValue placeholder="Selecione um usuário" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhum —</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1">
              <div className="flex items-center justify-between">
                <Label>Empresa Responsável (quem atende esta conta)</Label>
                <button
                  type="button"
                  onClick={() => setShowNewManagingCompany(v => !v)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  + Nova
                </button>
              </div>
              {editing && editing.managing_company_id && form.managing_company_id !== editing.managing_company_id && (
                <p className="text-xs text-amber-600">
                  ⚠️ Trocar de "{managingCompanyLabel(editing.managing_company_id)}" para "{managingCompanyLabel(form.managing_company_id) || '—'}" fica registrado no histórico da empresa.
                </p>
              )}
              {showNewManagingCompany && (
                <div className="flex items-center gap-2 mb-1">
                  <Input
                    autoFocus
                    placeholder="Ex: Neurosynx"
                    value={newManagingCompanyName}
                    onChange={e => setNewManagingCompanyName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateManagingCompany(); } }}
                  />
                  <Button type="button" size="sm" onClick={handleCreateManagingCompany} disabled={savingManagingCompany || !newManagingCompanyName.trim()}>
                    Add
                  </Button>
                </div>
              )}
              <Select value={form.managing_company_id || '__none__'} onValueChange={v => f(v === '__none__' ? '' : v, 'managing_company_id')}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nenhuma —</SelectItem>
                  {managingCompanies.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Observações</Label>
              <Textarea value={form.notes} onChange={e => f(e.target.value, 'notes')} placeholder="Observações sobre a empresa..." rows={3} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving || !isFormValid()} className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? 'Salvando...' : editing ? 'Salvar alterações' : 'Criar empresa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}